import Head from "next/head";
import { MenuId } from "../components/Header";
import Layout from "../components/Layout";
import styles from "./model-application-form.module.scss"
import { Button, Checkbox, FocusTrap, Group, List, Paper, Progress, rem, Select, Space, Text, Textarea, TextInput } from "@mantine/core";
import Link from "next/link";
import { useForm } from '@mantine/form'
import PhoneInput from "react-phone-input-2";
import { useState } from "react";
import 'react-phone-input-2/lib/style.css'

import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/sharp-solid-svg-icons";
import axios, { AxiosProgressEvent } from "axios";
import { okModal } from "../lib/utils";
import { modals } from "@mantine/modals";
import { consoleError } from "../lib/logger";
import FileChooser, { CompressedFile } from "../components/FileChooser";

export default function ApplicationForm()
{
    const [phoneNumberError, setPhoneNumberError] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [showUploadProgress, setShowUploadProgress] = useState(false);

    const [files, setFiles] = useState<CompressedFile[]>([]);
    const[fileError, setFileError] = useState(false);

    const form = useForm({
        initialValues: {firstName:'', lastName: '', email:'', phoneNumber:'', website:'', city:'', country:'', gender:'', pronouns:'', appearance:'', experience:'', portrait: false, nude:false, clothed:false, portraitClass:false, nudeClass:false, clothedClass:false, confirmation:false, privacy: false, genderOther:'', files:'' },
         validate: {
          email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
          phoneNumber: (value)=> 
          { 
            if (value.trim().length<5)
            {
                setPhoneNumberError(true);
                return "Please give a phone number";
            }
            setPhoneNumberError(false);

            return null;
          },
          files: (value)=>
          {
            if (files.length==0 || files.length>5)
            {
              setFileError(true);
              return "Please use at most 5 files";
            }
            setFileError(false);

            return null;
          }




        },
      });

      async function updateUploadProgress (e: AxiosProgressEvent)
      {
          setUploadProgress((e.progress??0)*100);
      }  


      async function submit(values: typeof form.values)
      {
        if (files.length==0)
        {
          return;
        }

        let formData = new FormData();

        Object.keys(form.values).forEach(x=>
          {
            formData.append(x, Object(form.values)[x])
          }
          );
        for (let i =0;i<files.length; i++)
        {

            formData.append("file_" + i, files[i].data);
        }

        setUploadProgress(0);
        setShowUploadProgress(true);
        setUploading(true);

        try
        {
        await axios.post("/n/api/modelapplication",
            formData,
            {
                headers:{'Content-Type': 'multipart/form-data'},
                onUploadProgress: updateUploadProgress,
                withCredentials: true
                                  
            });

            modals.open({
              title: 'Thank you!',
              children: <>
              <Text size="sm">Thank you for getting in touch! We&rsquo;ll email you if and when a modelling opportunity arises.</Text>
              <Button fullWidth onClick={(e)=> {modals.closeAll();}} mt="md">
                        Ok
                      </Button>
              </>
      
              
              });

            setShowUploadProgress(false);
        }
        catch(e)
        {
            consoleError(e);
            okModal("There was a problem saving your data. Please email us instead.");
        }
        finally
        {
            setShowUploadProgress(false);
            setUploading(false);
        }

      }

      function setPhoneNumber(value: string)
      {
        form.values.phoneNumber = value?value:'';
      }

      function setGender(value:string)
      {
        form.values.gender = value;

      }

      function filesUpdated(files: CompressedFile[])
      {
        setFiles(files);
      }


    return(
        <Layout menuId={MenuId.home}>
        <Head>
            <title>Model application form</title>
            <meta name='description' content="Application form if you'd like to model for Raw Umber Studios"/>
        </Head>
            
            <h1>Model application form</h1>
            <Text mb='sm'>
            Are you a professional life model or have an interest in life modelling?
            </Text>
            <Text mb='sm'>
We’ve been running online portrait and figure drawing sessions every week since March 2020 (you can see some examples <Link href='/videos/portrait-drawing-week-16'>here</Link> and <Link href='/videos/figure-drawing-week-27---mauricia/'>here</Link> and <Link href='https://rawumberstudios.com/videos/portrait-drawing-week-18-beatrice'>here</Link>. We also run life drawing classes at our studio in Stroud.
</Text>
            <Text mb='sm'>
If you’re interested in modelling for us, please complete the following form.
</Text>
            <Text mb='sm'>
We run photoshoots from our studios in Stroud (a 90 minute direct train ride from London). We pay up to £100 per shoot, plus travel costs of up to £55.
</Text>

<Text mb='sm'>
For modelling for our classes, we pay £20 / hour, including a paid lunch break if you&rsquo;re modelling all day, plus up to £10 travel costs per day.
</Text>

            <Text mb='sm'>
We aim for diversity within our life drawing sessions. We welcome applications from people of all ages, body shapes, and diverse backgrounds, including disabled models, Black, Asian, and Minority Ethnic (BAME) models, and LGBTQIA+ as well as non-binary models.
</Text>
            <Text mb='sm'>
Please note that the images that you upload below will only be used for internal purposes and will not be shared.            </Text>
<Space h='lg'/>
<Paper>
<FocusTrap active={true}>
          <form onSubmit={form.onSubmit( (values)=> submit(values))}>                            
              <TextInput label="First name" style={{maxWidth:'400px'}} type="text" required {...form.getInputProps('firstName')}/>
              <TextInput label="Last name" style={{maxWidth:'400px'}} type="text" required {...form.getInputProps('lastName')}/>
              <TextInput label="Email address" style={{maxWidth:'400px'}} type="email" required {...form.getInputProps('email')}/>
              <TextInput label="Website / Instagram" style={{maxWidth:'400px'}} type="text" {...form.getInputProps('website')}/>

<Text size='sm' fw={500}>Phone number</Text>
<PhoneInput
country='gb'
value={form.values.phoneNumber}
onChange={phone => setPhoneNumber(phone)}
preferredCountries={['gb']}

/>
{phoneNumberError && <Text c='red' size='xs' >Please enter a phone number</Text>}

<TextInput label="City" style={{maxWidth:'300px'}} required {...form.getInputProps('city')}/>
<TextInput label="Country" style={{maxWidth:'300px'}} required {...form.getInputProps('country')}/>
<Select required style={{maxWidth:'200px'}} label='Gender' {...form.getInputProps('gender')} data={[{value:'male', label:'Male'}, {value:'female', label:'Female'},{value:'other', label:'Other'}]} />
{form.values.gender=='other' && <TextInput style={{maxWidth:'200px'}} label="Gender (other)" {...form.getInputProps('genderOther')}/>}

<Select required style={{maxWidth:'200px'}} label='Pronouns' {...form.getInputProps('pronouns')} data={[{value:'him', label:'He/him'}, {value:'her', label:'She/her'},{value:'their', label:'They/their'}]} />

<Textarea label='Describe your general appearance. Do you have any unique features? Let us know' required {...form.getInputProps('appearance')} />
<Textarea label='Please provide details of any relevant experience and skills you have' {...form.getInputProps('experience')} />

<Text size='sm' fw={500}>I would like to be considered for the following modelling opportunities:</Text>
<Checkbox  label='Portrait - photoshoot' {...form.getInputProps('portrait')}/>
<Checkbox mt='xs' label='Figure (nude) - photoshoot' {...form.getInputProps('nude')}/>
<Checkbox mt='xs' mb='xs' label='Figure (clothed) - photoshoot' {...form.getInputProps('clothed')}/>
<Checkbox  label='Portrait - classes' {...form.getInputProps('portraitClass')}/>
<Checkbox mt='xs' label='Figure (nude) - classes' {...form.getInputProps('nudeClass')}/>
<Checkbox mt='xs' mb='xs' label='Figure (clothed) - classes' {...form.getInputProps('clothedClass')}/>


<Text size='sm' fw={500}>By completing this application form, you agree to the following:</Text>
<Checkbox  required label='I confirm I am 18 years old or over' {...form.getInputProps('confirmation')}/>
<Checkbox required mt='xs' label={<>I have read and agreed with the information above and the <Link href='/privacy-policy'>privacy policy</Link></>} {...form.getInputProps('privacy')}/>

<Space h='lg'/>
<Text size='sm' fw={500}>Please upload a handful of head and shoulder photographs:</Text>
<FileChooser maxFiles={5} uploadProgress={uploadProgress} uploading={uploading} filesUpdated={filesUpdated}/>

    {fileError && <Text c='red' size='xs' >Please upload between 1 and 5 files</Text>}

              <Button disabled={uploading} type='submit' mt='sm'>Submit</Button>
                             
            </form>            
            </FocusTrap>
            </Paper>
<Space h='xl'/>
           
            </Layout>
    );
}